import { Table } from '@/components';
import { CategoryState, ItemState, useGetExcludedGeoItemsQuery } from '@/redux';
import { useEffect, useState } from 'react';
import { useGeoItemsColumns } from './useGeoItemsColumns';
import { useItemListTreeDataSource } from './useItemListGridDataSource';

interface Props {
  communityId: string;
  floorplanId: string;
  isGeo?: boolean;
  editable?: boolean;
  selectedRowKeys?: React.Key[];
  onSelectionChange?: (map: Record<string, boolean>) => void;
}

export interface ItemTreeTableRowValues {
  categories: CategoryState[];
  item?: ItemState;
  children?: ItemTreeTableRowValues[];
  selectable?: boolean;
  isCategory?: boolean;
}

export function ItemListTabPanelGridTreeView(props: Props) {
  const {
    editable = false,
    selectedRowKeys,
    onSelectionChange,
    floorplanId,
  } = props;
  const { data: excludedIds = [], isFetching } = useGetExcludedGeoItemsQuery({
    floorplanId,
  });
  const { items, itemIds, loading } = useItemListTreeDataSource(props);

  const columns = useGeoItemsColumns();

  const [internalSelectedKeys, setInternalSelectedKeys] = useState<React.Key[]>(
    [],
  );

  useEffect(() => {
    if (!itemIds.length || isFetching) return;

    const initial = itemIds.filter((id) => !excludedIds.includes(String(id)));
    setInternalSelectedKeys(initial);
    if (onSelectionChange) {
      const map: Record<string, boolean> = {};
      itemIds.forEach((id) => (map[id] = initial.includes(id)));
      onSelectionChange(map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, items, excludedIds, isFetching]);

  const handleChange = (keys: React.Key[]) => {
    setInternalSelectedKeys(keys);
    if (onSelectionChange) {
      const map: Record<string, boolean> = {};
      itemIds.forEach((id) => (map[id] = keys.includes(id)));
      onSelectionChange(map);
    }
  };
  return (
    <Table<ItemTreeTableRowValues>
      name="ItemsAndCategoriesTreeView"
      hideDefaultActions
      loading={loading}
      columns={columns}
      dataSource={
        editable
          ? items
          : filterTree(items, selectedRowKeys ?? internalSelectedKeys)
      }
      rowKey={(row) => row.item?.id ?? `cat-${row.categories.at(-1)!.id}`}
      rowSelection={
        editable
          ? {
              type: 'checkbox',
              fixed: 'left',
              selectedRowKeys: selectedRowKeys ?? internalSelectedKeys,
              onChange: handleChange,
              checkStrictly: false,
              getCheckboxProps: (record: ItemTreeTableRowValues) => ({
                disabled: !record.selectable,
              }),
            }
          : undefined
      }
      expandable={{ defaultExpandAllRows: false, expandRowByClick: true }}
      pagination={{ defaultPageSize: 20 }}
    />
  );
}

function filterTree(
  nodes: ItemTreeTableRowValues[],
  selectedKeys: (string | number)[],
): ItemTreeTableRowValues[] {
  const selectedSet = new Set(selectedKeys.map(String));

  function walk(node: ItemTreeTableRowValues): ItemTreeTableRowValues | null {
    if (node.item) {
      return selectedSet.has(String(node.item.id)) ? { ...node } : null;
    }

    if (node.children) {
      const filteredChildren = node.children
        .map(walk)
        .filter((x): x is ItemTreeTableRowValues => x !== null);
      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
    }

    return null;
  }

  return nodes.map(walk).filter((x): x is ItemTreeTableRowValues => x !== null);
}
